import type {
  CreateWorkspaceScheduledBroadcastResponse,
  GetScheduledBroadcastsResponse,
  GetWorkspaceWorkflowItemsResponse,
  PublishBroadcastResponse,
  UpdateWorkspaceScheduledBroadcastResponse,
  WsWorkflowItem,
} from "../web-client/api/data-contracts";
import * as operations from "../web-client/api/operations";
import type { Transport } from "../web-client/transport";

export const workflowKeys = {
  items: (workspaceId: string) => ["workflows", "items", workspaceId] as const,
  scheduledBroadcasts: (workspaceId: string) => ["workflows", "scheduled-broadcasts", workspaceId] as const,
};

export function getWorkspaceWorkflowItems(
  transport: Transport,
  { workspaceId }: { workspaceId: string },
): Promise<GetWorkspaceWorkflowItemsResponse> {
  return operations.getWorkspacesWorkflowItems(transport, { workspaceId });
}

export function createWorkspaceWorkflowItem(
  transport: Transport,
  {
    workspaceId,
    contentId,
    workflowItemId,
    displayName,
    inputText,
  }: { workspaceId: string; contentId: string; workflowItemId: string; displayName?: string; inputText?: string },
): Promise<WsWorkflowItem> {
  return operations.postWorkspacesWorkflowItems(transport, {
    workspaceId,
    body: { workspaceId, contentId, workflowItemId, displayName, inputText },
  });
}

export function deleteWorkspaceWorkflowItem(
  transport: Transport,
  { workspaceId, workflowItemId }: { workspaceId: string; workflowItemId: string },
): Promise<void> {
  return operations.deleteWorkspacesWorkflowItems(transport, { workspaceId, workflowItemId });
}

export function getScheduledBroadcasts(
  transport: Transport,
  { workspaceId }: { workspaceId: string },
): Promise<GetScheduledBroadcastsResponse> {
  return operations.getWorkspacesScheduledBroadcasts(transport, { workspaceId });
}

export function createScheduledBroadcast(
  transport: Transport,
  {
    workspaceId,
    feedIds,
    workspaceMembershipIds,
    workflowItemId,
    date,
    cron,
    timezone,
  }: {
    workspaceId: string;
    feedIds?: string[];
    workspaceMembershipIds?: string[];
    workflowItemId: string;
    date?: string;
    cron?: string;
    timezone?: string;
  },
): Promise<CreateWorkspaceScheduledBroadcastResponse> {
  return operations.postWorkspacesTriggerSchedule(transport, {
    workspaceId,
    body: { feedIds, workspaceMembershipIds, workflowItemId, workspaceId, date, cron, timezone },
  });
}

export function updateScheduledBroadcast(
  transport: Transport,
  {
    workspaceId,
    scheduleId,
    feedIds,
    workspaceMembershipIds,
    date,
    cron,
    timezone,
  }: {
    workspaceId: string;
    scheduleId: string;
    feedIds?: string[];
    workspaceMembershipIds?: string[];
    date?: string;
    cron?: string;
    timezone?: string;
  },
): Promise<UpdateWorkspaceScheduledBroadcastResponse> {
  return operations.putWorkspacesTriggerSchedule(transport, {
    workspaceId,
    scheduleId,
    body: { feedIds, workspaceMembershipIds, date, cron, timezone },
  });
}

export function publishBroadcast(
  transport: Transport,
  {
    workspaceId,
    contentId,
    workspaceMembershipIds,
    feedIds,
    inputText,
    isSilent,
  }: {
    workspaceId: string;
    contentId: string;
    workspaceMembershipIds?: string[];
    feedIds?: string[];
    inputText?: string;
    isSilent?: boolean;
  },
): Promise<PublishBroadcastResponse> {
  return operations.postWorkspacesPublishBroadcast(transport, {
    workspaceId,
    body: { contentId, workspaceMembershipIds, feedIds, workspaceId, inputText, isSilent },
  });
}
